<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
}
</script>

<style>
.custom_badge_settings{
  background-color: rgba(1, 195, 218, 0.5) !important;
  font-size:0.686rem !important;
  line-height: 0 !important;
  right : -21px !important;
}

@media print {
  /* Ocultar todo el contenido de la app */
  body * {
    visibility: hidden !important;
  }

  /* Cambiar fondo gris por blanco */
  html, body {
    background: white !important;
  }

  #print-area {
    background: white !important;
  }

  /* Mostrar solo el contenido del contenedor imprimible */
  #print-container, #print-container * {
    visibility: visible !important;
  }

  /* Ubicar el contenedor imprimible al inicio */
  #print-container {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
  }

  @page {
    size: A3 landscape;
    margin: 1.5cm;
  }

  /* Ocultar botones */
  .btn {
    display: none !important;
  }

  /* Evitar cortes entre medio de componentes en saltos de página */
  .b-form-group,
  .form-group,
  .b-row,
  .row {
    page-break-inside: avoid !important;
  }

  /* Estilos para títulos */
  .print-title {
    font-size: 18pt !important;
    font-weight: bold !important;
    color: #fff !important;
    background-color: #004085 !important;
    padding: 4px 8px !important;
    border-radius: 4px !important;
  }

  /* Estilos para campos de texto (text area) en impresión */
  .print-text {
    display: block !important;
    white-space: pre-wrap !important;
    word-break: break-word !important;
    border: 1px solid #ccc;
    padding: 6px;
    min-height: 24px;
  }

  /* Evita que las columnas se expandan a 100% */
  .row {
    display: flex !important;
    flex-wrap: wrap !important;
  }

  [class*="col-"] {
    flex: 0 0 auto !important;
  }

  /* Bootstrap-Vue: respeta los anchos md, lg, etc. */
  .col-md-1 { width: 8.333333% !important; }
  .col-md-2 { width: 16.666667% !important; }
  .col-md-3 { width: 25% !important; }
  .col-md-4 { width: 33.333333% !important; }
  .col-md-5 { width: 41.666667% !important; }
  .col-md-6 { width: 50% !important; }
  .col-md-7 { width: 58.333333% !important; }
  .col-md-8 { width: 66.666667% !important; }
  .col-md-9 { width: 75% !important; }
  .col-md-10 { width: 83.333333% !important; }
  .col-md-11 { width: 91.666667% !important; }
  .col-md-12 { width: 100% !important; }
}

/* Fuerza el "Background graphics" ON para que no lo tenga que clickear el usuario al imprimir */
* {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

</style>
